import { useCallback, useEffect, useRef, useState } from 'react';

export type ResendCooldown = {
  /** Seconds left before another send is allowed; `0` once the control is usable again. */
  secondsRemaining: number;
  /** Whether a send is currently barred. Disable the control (and the handler) on this. */
  isCoolingDown: boolean;
  /** Restart the full window — call after a send that actually went out. */
  start: () => void;
  /** Clear the window immediately, stopping the countdown. */
  reset: () => void;
};

export type ResendCooldownOptions = {
  /**
   * Begin counting down from the first render. Set this wherever arriving at the screen *is* the send —
   * an MFA challenge whose code the login gate already mailed, a verification step reached straight from
   * a register that sent one. Read once, when the state initialises; later changes are ignored, so drive
   * a send that happens after mount through {@link ResendCooldown.start} instead.
   */
  startOnMount?: boolean;
};

/**
 * Countdown backing a "Resend code" control.
 *
 * Every resend endpoint enforces its own server-side window and answers a request inside it with
 * `Please wait N seconds before requesting another code`. This keeps the client's view of that window, so
 * the control is only offered when the request would actually be accepted — the alternative being a button
 * that is live from the moment the screen renders and is guaranteed to fail.
 *
 * The countdown is a self-clearing timeout per tick rather than one interval: it is owned by the effect, so
 * it stops when the component unmounts instead of outliving it and setting state on a component that is gone.
 *
 * @param durationSeconds - The window to count down, matching whatever the endpoint enforces.
 * @param options - See {@link ResendCooldownOptions}.
 * @param options.startOnMount - Begin counting down from the first render.
 */
export const useResendCooldown = (
  durationSeconds: number,
  { startOnMount = false }: ResendCooldownOptions = {},
): ResendCooldown => {
  const [secondsRemaining, setSecondsRemaining] = useState(startOnMount ? durationSeconds : 0);
  // Callers drive `start` from async mutation callbacks, so the send can settle after the user has closed the
  // modal or left the step. Writing state then is a no-op React 17 still warns about, so the window is only
  // opened while the owner is alive.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setSecondsRemaining((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsRemaining]);

  const start = useCallback(() => {
    if (isMountedRef.current) {
      setSecondsRemaining(durationSeconds);
    }
  }, [durationSeconds]);

  const reset = useCallback(() => {
    if (isMountedRef.current) {
      setSecondsRemaining(0);
    }
  }, []);

  return { secondsRemaining, isCoolingDown: secondsRemaining > 0, start, reset };
};
